import { z } from 'zod';
import { PAYMENT_METHODS, type PaymentMethod } from './partner-bank.ts';

/**
 * Condições de pagamento de uma obrigação (R6, ADR 0073).
 * - A condição **estruturada** do documento que origina a obrigação (pedido ou contrato) prevalece.
 * - A condição padrão do fornecedor na empresa (ADR 0016) só **sugere** o valor inicial, quando o
 *   documento não tem condição estruturada; a sugestão precisa ser confirmada por quem lança.
 * - Texto livre nunca calcula vencimento sozinho: ele acompanha a obrigação como referência.
 * - A condição aplicada é fotografada na obrigação, com a origem: mudar o cadastro do fornecedor
 *   depois não reescreve obrigações já constituídas.
 */

export interface StructuredTerms {
  method: PaymentMethod;
  /** Dias depois da data base até o primeiro vencimento. */
  netDays: number;
  installments: number;
  /** Dia fixo do mês para os vencimentos, quando houver. */
  dueDay: number | null;
}

export type TermsSource = 'origin_document' | 'partner_default' | 'manual';

export interface TermsSnapshot {
  terms: StructuredTerms | null;
  source: TermsSource | null;
  /** A condição é sugestão e precisa da confirmação de quem lança a obrigação. */
  needsConfirmation: boolean;
  /** Texto livre do documento de origem, guardado como veio (não calcula nada). */
  originText: string | null;
  originKind: 'purchase_order' | 'contract' | 'none';
}

export function validTerms(t: StructuredTerms | null | undefined): t is StructuredTerms {
  return (
    !!t &&
    Number.isInteger(t.netDays) &&
    t.netDays >= 0 &&
    t.netDays <= 365 &&
    Number.isInteger(t.installments) &&
    t.installments >= 1 &&
    t.installments <= 60 &&
    (t.dueDay == null || (Number.isInteger(t.dueDay) && t.dueDay >= 1 && t.dueDay <= 31))
  );
}

/** Qual condição vale para a obrigação nascida de um documento, e de onde ela veio. */
export function resolvePaymentTerms(input: {
  origin: {
    kind: 'purchase_order' | 'contract' | 'none';
    terms?: StructuredTerms | null;
    text?: string | null;
  };
  partnerDefault?: StructuredTerms | null;
}): TermsSnapshot {
  const originText = input.origin.text?.trim() || null;
  const base = { originText, originKind: input.origin.kind };
  if (validTerms(input.origin.terms)) {
    return {
      ...base,
      terms: input.origin.terms,
      source: 'origin_document',
      needsConfirmation: false,
    };
  }
  if (validTerms(input.partnerDefault)) {
    return {
      ...base,
      terms: input.partnerDefault,
      source: 'partner_default',
      needsConfirmation: true,
    };
  }
  return { ...base, terms: null, source: null, needsConfirmation: true };
}

function addDaysIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Mesmo dia em outro mês; dia que não existe no mês vira o último dia dele. */
function withDay(year: number, month: number, day: number): string {
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day, last))).toISOString().slice(0, 10);
}

/**
 * Vencimentos a partir da data base (emissão do documento ou aceite): o primeiro em `netDays`, os
 * seguintes a cada mês. Com dia fixo, cada vencimento vai para esse dia, no mesmo mês ou no
 * seguinte, nunca antes da data calculada.
 */
export function paymentDueDates(terms: StructuredTerms, baseDate: string): string[] {
  if (!validTerms(terms)) throw new Error('Condição de pagamento inválida');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(baseDate)) throw new Error('Data base inválida');
  const first = addDaysIso(baseDate, terms.netDays);
  const [y, m, d] = first.split('-').map(Number) as [number, number, number];
  const dates: string[] = [];
  for (let i = 0; i < terms.installments; i++) {
    if (terms.dueDay == null) {
      dates.push(withDay(y, m - 1 + i, d));
    } else {
      let candidate = withDay(y, m - 1 + i, terms.dueDay);
      const floor = withDay(y, m - 1 + i, d);
      if (candidate < floor) candidate = withDay(y, m + i, terms.dueDay);
      dates.push(candidate);
    }
  }
  return dates;
}

// ----- Condição estruturada nos documentos de origem (ADR 0104) -----

/**
 * Condição estruturada informada na proposta da cotação, no contrato ou no aditivo (ADR 0104, D1):
 * os mesmos campos do padrão do fornecedor. Parcelas são mensais e iguais; o dia fixo é opcional.
 */
export const structuredTermsInput = z.object({
  method: z.enum(PAYMENT_METHODS),
  netDays: z.number().int().min(0).max(365),
  installments: z.number().int().min(1).max(60),
  dueDay: z
    .number()
    .int()
    .min(1)
    .max(31)
    .nullable()
    .optional()
    .transform((v) => v ?? null),
});

/** As colunas `terms_*` de um documento viram a condição (ou nada, se ele não tem condição). */
export function termsFromColumns(row: {
  termsMethod: string | null;
  termsNetDays: number | null;
  termsInstallments: number | null;
  termsDueDay: number | null;
}): StructuredTerms | null {
  if (row.termsMethod == null || row.termsNetDays == null || row.termsInstallments == null) {
    return null;
  }
  return {
    method: row.termsMethod as PaymentMethod,
    netDays: row.termsNetDays,
    installments: row.termsInstallments,
    dueDay: row.termsDueDay,
  };
}

/** A condição nas colunas `terms_*` (todas vazias quando não há condição). */
export function termsColumns(t: StructuredTerms | null | undefined) {
  return {
    termsMethod: t?.method ?? null,
    termsNetDays: t?.netDays ?? null,
    termsInstallments: t?.installments ?? null,
    termsDueDay: t ? (t.dueDay ?? null) : null,
  };
}

export function sameTerms(a: StructuredTerms | null, b: StructuredTerms | null): boolean {
  if (!a || !b) return a === b;
  return (
    a.method === b.method &&
    a.netDays === b.netDays &&
    a.installments === b.installments &&
    (a.dueDay ?? null) === (b.dueDay ?? null)
  );
}

/**
 * O título se afasta da condição estruturada do pedido ou do contrato (D7)? Conta a condição e a
 * data base, que é sempre a emissão da nota (D2). Sem condição na origem, não há o que comparar.
 * Quando se afasta, quem lança escreve o motivo.
 */
export function termsOverridden(input: {
  origin: StructuredTerms | null;
  applied: StructuredTerms;
  baseDate: string;
  issueDate: string | null | undefined;
}): boolean {
  if (!validTerms(input.origin)) return false;
  if (!sameTerms(input.origin, input.applied)) return true;
  return !!input.issueDate && input.issueDate !== input.baseDate;
}

/**
 * Vencimentos que a nota declara (duplicatas do XML) contra os calculados pela condição do título
 * (D7): quando diferem, a tela avisa com os da nota. O título continua seguindo a condição; nada é
 * bloqueado. Sem vencimentos na nota, não há aviso.
 */
export function invoiceDueDatesDivergence(
  terms: StructuredTerms,
  baseDate: string,
  invoiceDueDates: readonly string[],
): { calculated: string[]; invoice: string[] } | null {
  if (invoiceDueDates.length === 0 || !validTerms(terms)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(baseDate)) return null;
  const calculated = paymentDueDates(terms, baseDate);
  const invoice = [...invoiceDueDates].sort();
  const same = calculated.length === invoice.length && calculated.every((d, i) => d === invoice[i]);
  return same ? null : { calculated, invoice };
}
